import { LogOut } from "lucide-react";
import { useCurrentUser, useLogout } from "@/features/auth/hooks";
import { initialsOf } from "@/features/auth/permissions";

const signOutClass = "rounded p-1.5 text-faint hover:bg-nav-hover hover:text-ink disabled:opacity-40";

/**
 * Who is signed in, and "Sign out". A plain user needs no label; a workbench
 * admin (who manages people) is told so. `compact`: the avatar and the
 * sign-out button stacked, for the collapsed sidebar.
 */
export function UserMenu({ compact = false }: { compact?: boolean }) {
  const { data: user } = useCurrentUser();
  const logout = useLogout();

  if (!user) return null;
  const admin = user.role === "admin";
  const avatar = (
    <span
      title={compact ? `${user.name} · ${user.email}` : undefined}
      className="flex size-7 shrink-0 items-center justify-center rounded-full bg-avatar text-[11px] font-semibold"
    >
      {initialsOf(user.name, user.email)}
    </span>
  );
  const signOut = (
    <button
      type="button"
      onClick={() => logout.mutate()}
      disabled={logout.isPending}
      aria-label="Sign out"
      title="Sign out"
      className={signOutClass}
    >
      <LogOut size={14} />
    </button>
  );

  if (compact)
    return (
      <div className="mx-auto flex flex-col items-center gap-1.5">
        {avatar}
        {signOut}
      </div>
    );
  return (
    <div className="flex items-center gap-2.5 px-1.5">
      {avatar}
      <span className="flex min-w-0 flex-1 flex-col">
        <span className="truncate text-[13px] font-medium" title={user.email}>
          {user.name}
        </span>
        {admin && <span className="text-[11px] text-faint">Workbench admin</span>}
      </span>
      {signOut}
    </div>
  );
}
